import { useState, useEffect } from 'react';
import { getNotificationSettings, updateNotificationSettings, testEmailNotification, testSmsNotification } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Bell, Mail, Phone, TestTube, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const SettingsModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [settings, setSettings] = useState({
    notification_email: '',
    notification_phone: '',
    auto_remind_days: 3,
    sms_enabled: false
  });

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const res = await getNotificationSettings();
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings({
        notification_email: settings.notification_email,
        notification_phone: settings.notification_phone,
        auto_remind_days: settings.auto_remind_days
      });
      toast.success('Settings saved');
      onClose();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await testEmailNotification();
      toast.success('Test email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      await testSmsNotification();
      toast.success('Test SMS sent! Check your phone.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5 text-accent" />
            Notification Settings
          </DialogTitle>
          <DialogDescription>
            Configure how you receive follow-up reminders
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Email Notifications */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-500" />
                Email Notifications
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={settings.notification_email || ''}
                  onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                  className="flex-1"
                  data-testid="notification-email-input"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleTestEmail}
                  disabled={testingEmail || !settings.notification_email}
                  data-testid="test-email-btn"
                >
                  {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Receive reminder emails at this address
              </p>
            </div>

            {/* SMS Notifications */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-500" />
                SMS Notifications
                {settings.sms_enabled && (
                  <span className="text-xs text-green-500 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={settings.notification_phone || ''}
                  onChange={(e) => setSettings({ ...settings, notification_phone: e.target.value })}
                  className="flex-1"
                  data-testid="notification-phone-input"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleTestSms}
                  disabled={testingSms || !settings.notification_phone || !settings.sms_enabled}
                  data-testid="test-sms-btn"
                >
                  {testingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {settings.sms_enabled 
                  ? "Enter your phone number with country code (e.g., +1234567890)"
                  : "SMS requires Twilio configuration. Contact admin to enable."}
              </p>
            </div>

            {/* Auto-Remind Days */}
            <div className="space-y-3">
              <Label className="flex items-center justify-between">
                <span>Auto-Remind After Inactivity</span>
                <span className="text-accent font-mono">{settings.auto_remind_days} days</span>
              </Label>
              <Slider
                value={[settings.auto_remind_days]}
                onValueChange={(v) => setSettings({ ...settings, auto_remind_days: v[0] })}
                min={0}
                max={14}
                step={1}
                className="w-full"
                data-testid="auto-remind-slider"
              />
              <p className="text-xs text-muted-foreground">
                {settings.auto_remind_days === 0 
                  ? "Auto-reminders disabled" 
                  : `Get reminded about leads with no contact for ${settings.auto_remind_days}+ days`}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                data-testid="save-settings-btn"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
